/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import { services } from '../../services';

interface TestnetUser {
  id?: string;
  _id?: string;
  username?: string;
  email?: string;
  twitterHandle?: string;
  telegramHandle?: string;
  // Captured by the platform early-access form on /marketplace
  investorType?: string;
  indicativeTicket?: string;
  submittedAt?: string;
  createdAt?: string;
}

const buildTwitterUrl = (handle?: string) => {
  if (!handle) return undefined;
  const trimmed = handle.trim();
  if (!trimmed) return undefined;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const clean = trimmed.replace(/^@+/, '');
  return `https://twitter.com/${clean}`;
};

const buildTelegramUrl = (handle?: string) => {
  if (!handle) return undefined;
  const trimmed = handle.trim();
  if (!trimmed) return undefined;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const clean = trimmed.replace(/^@+/, '');
  return `https://t.me/${clean}`;
};

const formatDateTime = (iso?: string) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch (_) {
    return iso;
  }
};

const TestnetSignupPage: React.FC = () => {
  const [users, setUsers] = useState<TestnetUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<TestnetUser | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const fetchUsers = async (page: number = currentPage) => {
    setLoading(true);
    setError(null);
    try {
      const url = `${API_ENDPOINTS.TESTNET_SIGNUP.LIST}?page=${page}&limit=${itemsPerPage}`;
      const response = await services.get(url);
      // Normalize various possible shapes
      const data = response?.data;
      let rows: any = [];
      if (Array.isArray(data)) rows = data;
      else if (Array.isArray(data?.data)) rows = data.data;
      else if (data?.data && typeof data.data === 'object') rows = [data.data];
      else rows = [];
      setUsers(rows);

      // Parse pagination meta robustly
      const pageMeta: any = data?.pagination || data?.meta || data?.pageInfo || {};
      if (pageMeta && (pageMeta.pages || pageMeta.totalPages)) {
        setTotalPages(Number(pageMeta.pages || pageMeta.totalPages) || 1);
        setCurrentPage(Number(pageMeta.page || pageMeta.currentPage || page) || 1);
        setTotalItems(Number(pageMeta.total || pageMeta.totalItems || rows.length) || rows.length);
      } else {
        // Fallbacks when API does not return meta but may return total
        const total = Number(data?.total) || Number(data?.count) || rows.length;
        setTotalItems(total);
        const pages = Math.max(1, Math.ceil(total / itemsPerPage));
        setTotalPages(pages);
        setCurrentPage(page);
      }
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to load testnet signup users');
    } finally {
      setLoading(false);
    }
  };

  const openView = async (id?: string) => {
    if (!id) return;
    setViewLoading(true);
    try {
      const response = await services.get(API_ENDPOINTS.TESTNET_SIGNUP.GET_BY_ID(id));
      const data = response?.data;
      let record: any = null;
      if (data?.data) record = data.data;
      else record = data;
      setViewing(record || null);
    } catch (err) {
      setViewing(null);
    } finally {
      setViewLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h3 className='text-2xl font-bold font-dm-serif' style={{ color: '#ede8c5' }}>Testnet Signup Management</h3>
          <p className='font-work-sans mt-1' style={{ color: '#b8c5c2' }}>Manage and view all testnet signup users</p>
        </div>
      </div>

      {error && (
        <div className='px-4 py-3 rounded-md' style={{ backgroundColor: 'rgba(220,38,38,0.1)', color: '#fecaca', border: '1px solid rgba(220,38,38,0.3)' }}>
          {error}
        </div>
      )}

      <div
        className='rounded-lg overflow-hidden shadow-lg transition-all duration-300'
        style={{
          backgroundColor: '#0B222C',
          border: '2px solid #095D52',
          boxShadow:
            '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
          backgroundImage:
            'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
        }}
      >
        <div className='overflow-x-auto'>
          <table className='min-w-full' style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead
              style={{
                backgroundColor: '#095D52',
                backgroundImage:
                  'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              }}
            >
            <tr>
                <th className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans' style={{ color: '#ede8c5' }}>Name</th>
                <th className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans' style={{ color: '#ede8c5' }}>Email</th>
                <th className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans' style={{ color: '#ede8c5' }}>Twitter</th>
                <th className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans' style={{ color: '#ede8c5' }}>Telegram</th>
                <th className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans' style={{ color: '#ede8c5' }}>Submitted</th>
                <th className='px-6 py-4 text-left text-xs font-bold uppercase tracking-wider font-work-sans' style={{ color: '#ede8c5' }}>Actions</th>
            </tr>
            </thead>
            <tbody style={{ backgroundColor: '#0B222C' }}>
            {loading ? (
              <tr>
                <td colSpan={6} className='px-4 py-8 text-center' style={{ color: '#b8c5c2' }}>Loading...</td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={6} className='px-4 py-8 text-center' style={{ color: '#b8c5c2' }}>No records</td>
              </tr>
            ) : (
              users.map((u, idx) => {
                const id = (u as any).id || u._id;
                const twitterUrl = buildTwitterUrl(u.twitterHandle);
                const telegramUrl = buildTelegramUrl(u.telegramHandle);
                return (
                  <tr
                    key={id}
                    className='transition-colors'
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#095D52')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    style={{ boxShadow: idx > 0 ? 'inset 0 1px 0 rgba(187, 228, 37, 0.2)' : undefined }}
                  >
                    <td className='px-6 py-4 whitespace-nowrap' style={{ color: '#ede8c5' }}>{u.username || '-'}</td>
                    <td className='px-6 py-4 whitespace-nowrap' style={{ color: '#ede8c5' }}>{u.email || '-'}</td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      {twitterUrl ? (
                        <a
                          href={twitterUrl}
                          target='_blank'
                          rel='noreferrer'
                          className='underline'
                          style={{ color: '#BAE325' }}
                        >
                          {u.twitterHandle}
                        </a>
                      ) : (
                        <span style={{ color: '#b8c5c2' }}>-</span>
                      )}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      {telegramUrl ? (
                        <a
                          href={telegramUrl}
                          target='_blank'
                          rel='noreferrer'
                          className='underline'
                          style={{ color: '#BAE325' }}
                        >
                          {u.telegramHandle}
                        </a>
                      ) : (
                        <span style={{ color: '#b8c5c2' }}>-</span>
                      )}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap' style={{ color: '#ede8c5' }}>{formatDateTime(u.submittedAt || u.createdAt)}</td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <div className='flex space-x-2'>
                        <button
                          onClick={() => openView(String(id))}
                          className='p-2 rounded-lg transition-all duration-200 hover:scale-105 focus:outline-none'
                          style={{
                            backgroundColor: '#0B222C',
                            border: '1px solid #095D52',
                            color: '#ede8c5',
                            boxShadow:
                              '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#095D52';
                            e.currentTarget.style.borderColor = '#bbe425';
                            e.currentTarget.style.boxShadow =
                              '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#0B222C';
                            e.currentTarget.style.borderColor = '#095D52';
                            e.currentTarget.style.boxShadow =
                              '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)';
                          }}
                          title='View Details'
                        >
                          <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className='mt-4 flex items-center justify-between px-2'>
        <div className='text-sm font-work-sans' style={{ color: '#b8c5c2' }}>
          {totalItems > 0 ? (
            <>Showing {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}</>
          ) : (
            <>No records</>
          )}
        </div>
        <div className='flex items-center space-x-2'>
          <button
            onClick={() => currentPage > 1 && fetchUsers(currentPage - 1)}
            disabled={currentPage <= 1}
            className='px-3 py-1 rounded-md'
            style={{
              backgroundColor: currentPage <= 1 ? 'rgba(9,93,82,0.15)' : '#095D52',
              color: '#ede8c5',
              border: '1px solid rgba(187,228,37,0.3)'
            }}
          >
            Prev
          </button>
          {Array.from({ length: totalPages }).slice(Math.max(0, currentPage - 3), Math.max(0, currentPage - 3) + 5).map((_, idx) => {
            const start = Math.max(1, currentPage - 2);
            const pageNum = Math.min(totalPages, start + idx);
            if (pageNum < 1) return null;
            return (
              <button
                key={`pg-${pageNum}`}
                onClick={() => fetchUsers(pageNum)}
                className='px-3 py-1 rounded-md'
                style={{
                  backgroundColor: pageNum === currentPage ? '#BAE325' : 'rgba(9,93,82,0.25)',
                  color: pageNum === currentPage ? '#0B222C' : '#ede8c5',
                  border: '1px solid rgba(187,228,37,0.35)'
                }}
              >
                {pageNum}
              </button>
            );
          })}
          <button
            onClick={() => currentPage < totalPages && fetchUsers(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className='px-3 py-1 rounded-md'
            style={{
              backgroundColor: currentPage >= totalPages ? 'rgba(9,93,82,0.15)' : '#095D52',
              color: '#ede8c5',
              border: '1px solid rgba(187,228,37,0.3)'
            }}
          >
            Next
          </button>
        </div>
      </div>

      {viewing && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center'
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }}
          aria-modal='true'
          role='dialog'
        >
          <div
            className='w-full max-w-xl rounded-xl overflow-hidden'
            style={{
              border: '2px solid #095D52',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(187, 228, 37, 0.12)'
            }}
          >
            <div
              className='flex items-center space-x-3'
              style={{ backgroundColor: '#095D52', padding: '16px 20px' }}
            >
              <div
                className='flex items-center justify-center rounded-md'
                style={{ width: 36, height: 36, backgroundColor: 'rgba(11,34,44,0.25)', border: '1px solid rgba(187,228,37,0.25)' }}
              >
                <svg className='w-5 h-5' fill='none' stroke='#ede8c5' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5.121 17.804A13.937 13.937 0 0112 15c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z' />
                </svg>
              </div>
              <h4 className='text-lg font-semibold font-dm-serif' style={{ color: '#ede8c5' }}>User Details</h4>
            </div>
            <div className='p-6' style={{ backgroundColor: '#0B222C' }}>
              {viewLoading ? (
                <div className='py-6 text-center' style={{ color: '#b8c5c2' }}>Loading...</div>
              ) : (
                <div className='space-y-4'>
                  <div className='grid grid-cols-3 gap-3 items-start pb-3' style={{ borderBottom: '1px dashed rgba(187, 228, 37, 0.15)' }}>
                    <div className='text-sm font-medium' style={{ color: '#8fb3a8' }}>Name</div>
                    <div className='col-span-2 text-sm' style={{ color: '#ede8c5' }}>{viewing.username || '-'}</div>
                  </div>
                  <div className='grid grid-cols-3 gap-3 items-start pb-3' style={{ borderBottom: '1px dashed rgba(187, 228, 37, 0.15)' }}>
                    <div className='text-sm font-medium' style={{ color: '#8fb3a8' }}>Email</div>
                    <div className='col-span-2 text-sm' style={{ color: '#ede8c5' }}>{viewing.email || '-'}</div>
                  </div>
                  <div className='grid grid-cols-3 gap-3 items-start pb-3' style={{ borderBottom: '1px dashed rgba(187, 228, 37, 0.15)' }}>
                    <div className='text-sm font-medium' style={{ color: '#8fb3a8' }}>Twitter</div>
                    <div className='col-span-2 text-sm'>
                      {buildTwitterUrl(viewing.twitterHandle) ? (
                        <a
                          href={buildTwitterUrl(viewing.twitterHandle)}
                          target='_blank'
                          rel='noreferrer'
                          className='inline-flex items-center space-x-2 px-3 py-1 rounded-md'
                          style={{ color: '#BAE325', backgroundColor: 'rgba(9,93,82,0.25)', border: '1px solid rgba(187, 228, 37, 0.25)' }}
                        >
                          <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
                            <path d='M22.46 6c-.77.35-1.6.58-2.46.69a4.29 4.29 0 001.88-2.37 8.59 8.59 0 01-2.72 1.04 4.28 4.28 0 00-7.3 3.9A12.14 12.14 0 013 4.9a4.27 4.27 0 001.33 5.71c-.64-.02-1.25-.2-1.78-.49v.05a4.28 4.28 0 003.44 4.19c-.3.08-.62.12-.95.12-.23 0-.46-.02-.68-.06a4.28 4.28 0 003.99 2.97A8.58 8.58 0 012 19.54a12.1 12.1 0 006.56 1.92c7.88 0 12.2-6.53 12.2-12.2 0-.18-.01-.36-.02-.54A8.72 8.72 0 0022.46 6z' />
                          </svg>
                          <span>{viewing.twitterHandle}</span>
                        </a>
                      ) : (
                        <span style={{ color: '#b8c5c2' }}>-</span>
                      )}
                    </div>
                  </div>
                  <div className='grid grid-cols-3 gap-3 items-start pb-3' style={{ borderBottom: '1px dashed rgba(187, 228, 37, 0.15)' }}>
                    <div className='text-sm font-medium' style={{ color: '#8fb3a8' }}>Telegram</div>
                    <div className='col-span-2 text-sm'>
                      {buildTelegramUrl(viewing.telegramHandle) ? (
                        <a
                          href={buildTelegramUrl(viewing.telegramHandle)}
                          target='_blank'
                          rel='noreferrer'
                          className='inline-flex items-center space-x-2 px-3 py-1 rounded-md'
                          style={{ color: '#BAE325', backgroundColor: 'rgba(9,93,82,0.25)', border: '1px solid rgba(187, 228, 37, 0.25)' }}
                        >
                          <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
                            <path d='M9.03 15.47l-.38 5.33c.54 0 .77-.23 1.05-.5l2.53-2.42 5.24 3.84c.96.53 1.64.25 1.88-.89l3.41-16c.3-1.45-.52-2.02-1.45-1.67L2.6 9.68c-1.41.55-1.39 1.33-.24 1.68l5.44 1.7 12.63-7.97c.59-.36 1.13-.16.69.21L9.03 15.47z' />
                          </svg>
                          <span>{viewing.telegramHandle}</span>
                        </a>
                      ) : (
                        <span style={{ color: '#b8c5c2' }}>-</span>
                      )}
                    </div>
                  </div>
                  <div className='grid grid-cols-3 gap-3 items-start pb-3' style={{ borderBottom: '1px dashed rgba(187, 228, 37, 0.15)' }}>
                    <div className='text-sm font-medium' style={{ color: '#8fb3a8' }}>Investor Type</div>
                    <div className='col-span-2 text-sm' style={{ color: '#ede8c5' }}>{viewing.investorType || '-'}</div>
                  </div>
                  <div className='grid grid-cols-3 gap-3 items-start pb-3' style={{ borderBottom: '1px dashed rgba(187, 228, 37, 0.15)' }}>
                    <div className='text-sm font-medium' style={{ color: '#8fb3a8' }}>Indicative Ticket</div>
                    <div className='col-span-2 text-sm' style={{ color: '#ede8c5' }}>{viewing.indicativeTicket || '-'}</div>
                  </div>
                  <div className='grid grid-cols-3 gap-3 items-start'>
                    <div className='text-sm font-medium' style={{ color: '#8fb3a8' }}>Submitted</div>
                    <div className='col-span-2 text-sm' style={{ color: '#ede8c5' }}>{formatDateTime(viewing.submittedAt || viewing.createdAt)}</div>
                  </div>
                </div>
              )}
              <div className='mt-8 flex justify-end space-x-3'>
                {buildTwitterUrl(viewing?.twitterHandle) && (
                  <a
                    href={buildTwitterUrl(viewing?.twitterHandle)}
                    target='_blank'
                    rel='noreferrer'
                    className='px-4 py-2 rounded-md'
                    style={{ backgroundColor: 'rgba(9,93,82,0.25)', color: '#BAE325', border: '1px solid rgba(187,228,37,0.3)' }}
                  >
                    Open Twitter
                  </a>
                )}
                {buildTelegramUrl(viewing?.telegramHandle) && (
                  <a
                    href={buildTelegramUrl(viewing?.telegramHandle)}
                    target='_blank'
                    rel='noreferrer'
                    className='px-4 py-2 rounded-md'
                    style={{ backgroundColor: 'rgba(9,93,82,0.25)', color: '#BAE325', border: '1px solid rgba(187,228,37,0.3)' }}
                  >
                    Open Telegram
                  </a>
                )}
                <button
                  onClick={() => setViewing(null)}
                  className='px-4 py-2 rounded-md'
                  style={{ backgroundColor: '#095D52', color: '#ede8c5', border: '1px solid rgba(187,228,37,0.3)' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestnetSignupPage;


